// Donation-page logo checks over the backend `DonationLogo` format, size and dimension bounds and
// messages, which are generated into constants.gen.ts.
//
// These are for immediate feedback, never the guardrail. The bytes go browser-to-storage without
// passing through the API, and a presigned PUT constrains neither length nor content — so the
// authoritative check is the backend's `DonationLogoVerifier`, which reads the object that actually
// landed and re-checks its true size, real format (magic bytes) and pixel dimensions before the key is
// allowed to become durable.

import {
  DONATION_LOGO_ACCEPTED_TYPES,
  DONATION_LOGO_EMPTY_FILE_MESSAGE,
  DONATION_LOGO_MAX_BYTES,
  DONATION_LOGO_MAX_DIMENSION,
  DONATION_LOGO_TOO_LARGE_MESSAGE,
  DONATION_LOGO_TOO_MANY_PIXELS_MESSAGE,
  DONATION_LOGO_WRONG_FORMAT_MESSAGE,
} from '../generated/constants.gen';

/** The `accept` attribute for the file `<input>`, so the OS picker pre-filters to the allowed types. */
export const DONATION_LOGO_ACCEPT_ATTR = DONATION_LOGO_ACCEPTED_TYPES.join(',');

/**
 * Validate a chosen file's MIME type and byte size against the shared bounds. Returns a
 * treasurer-readable error string when the file is a disallowed format, empty, or over the size cap;
 * returns `null` when the file is acceptable. Mirrors the backend `DonationLogo.Validate`, which is the
 * authoritative check the mint endpoint enforces (the client is never the guardrail).
 */
export function donationLogoError(file: { type: string; size: number }): string | null {
  if (!DONATION_LOGO_ACCEPTED_TYPES.includes(file.type)) {
    return DONATION_LOGO_WRONG_FORMAT_MESSAGE;
  }
  if (file.size <= 0) {
    return DONATION_LOGO_EMPTY_FILE_MESSAGE;
  }
  if (file.size > DONATION_LOGO_MAX_BYTES) {
    return DONATION_LOGO_TOO_LARGE_MESSAGE;
  }
  return null;
}

/**
 * Validate a chosen image's pixel dimensions against the shared cap. Returns a treasurer-readable error
 * when the image is too big to display (or could not be decoded at all — the browser reports 0x0 for a
 * file that is not really an image); returns `null` when it is acceptable. Mirrors the backend check,
 * which reads the dimensions out of the stored object's header and is the authoritative one.
 */
export function donationLogoDimensionError(size: { width: number; height: number }): string | null {
  if (!(size.width > 0) || !(size.height > 0)) {
    return 'That file could not be read as an image. Choose a PNG, JPG, or WebP.';
  }
  if (size.width > DONATION_LOGO_MAX_DIMENSION || size.height > DONATION_LOGO_MAX_DIMENSION) {
    return DONATION_LOGO_TOO_MANY_PIXELS_MESSAGE;
  }
  return null;
}
